import * as React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import CashCollectionForm from 'src/features/cashCollections/components/forms/CashCollectionForm';
import {
    CASH_COLLECTION_RESOURCE,
    createCashCollectionFormSchema,
    type CreateCashCollectionFormSchemaValues,
} from 'src/features/cashCollections/hooks/useCreateCashCollection';
import { useUpdateCashCollection } from 'src/features/cashCollections/hooks/useUpdateCashCollection';
import { cashCollectionsMessages } from 'src/features/cashCollections/utils/messages';
import { formatLocalISO } from 'src/features/cashCollections/utils/utils';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { type DataTableNewEntryProps } from 'src/registry/components/table';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { CashCollections } from 'src/features/cashCollections/types/cashCollections';

interface EditCashCollectionProps extends DataTableNewEntryProps<any> {
    selectedCashCollection: CashCollections;
    isEditModalOpen: boolean;
    setIsEditModalOpen: (isOpen: boolean) => void;
}
function EditCashCollection({ selectedCashCollection, isEditModalOpen, setIsEditModalOpen }: EditCashCollectionProps) {
    const { formatMessage } = useIntl();

    const { toast } = useToast();

    const { mutateAsync, isPending } = useUpdateCashCollection({
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [CASH_COLLECTION_RESOURCE],
            });
            setIsEditModalOpen(false);
            form.reset();
            toast({
                title: formatMessage(cashCollectionsMessages.cashCollectionUpdated),
                description: formatMessage(cashCollectionsMessages.cashCollectionUpdatedSuccessfully),
            });
        },
        onError: (e) => {
            const error = Object.entries(e?.response?.data || {})[0];
            const errorCode = e?.response?.data.code;
            const errorCodeConfiguration = 'parking_not_configured_for_cash_collection';

            const errorMessage =
                errorCode == errorCodeConfiguration
                    ? formatMessage(cashCollectionsMessages.cashCollectionConfigurationError)
                    : error
                      ? `${error[0]}, ${error[1][0]}`
                      : formatMessage(generalErrorMessages.somethingWentWrongRetry);

            toast({
                variant: 'destructive',
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: errorMessage,
            });
        },
    });

    const selectedDate = selectedCashCollection.date ? new Date(selectedCashCollection.date) : new Date();

    const normalizeKey = (denom: string) => denom.replace(/\./g, '_');

    const normalizedNominals: Record<string, number> = Object.fromEntries(
        Object.entries(selectedCashCollection.nominal_amounts ?? {}).map(([k, v]) => [normalizeKey(k), v as number]),
    );

    const defaultValues = React.useMemo(() => {
        return {
            collected_by: selectedCashCollection.collected_by,
            parking: selectedCashCollection.parking.id,
            paystation: selectedCashCollection.paystation.id,
            status: selectedCashCollection.status,
            type: selectedCashCollection.type,
            nominal_amounts: normalizedNominals,
            description: selectedCashCollection.description ?? '',
            files: undefined,
            date: formatLocalISO(selectedDate),
            time: `${String(selectedDate.getHours()).padStart(2, '0')}:${String(selectedDate.getMinutes()).padStart(
                2,
                '0',
            )}`,
        };
    }, [selectedCashCollection, selectedDate]);

    const form = useForm<CreateCashCollectionFormSchemaValues>({
        resolver: zodResolver(createCashCollectionFormSchema),
        defaultValues,
    });

    const onSubmit: SubmitHandler<CreateCashCollectionFormSchemaValues> = async (
        values: CreateCashCollectionFormSchemaValues,
    ) => {
        const dirtyFields = form.formState.dirtyFields;
        const editedFields = Object.keys(dirtyFields).reduce(
            (acc, key) => {
                const typedKey = key as keyof Omit<CreateCashCollectionFormSchemaValues, 'time'>;
                acc[typedKey] = values[typedKey];
                return acc;
            },
            {} as Partial<Record<keyof CreateCashCollectionFormSchemaValues, any>>,
        );

        const denormalizeKey = (key: string) => key.replace(/_/g, '.');
        const fixedNominals = Object.fromEntries(
            Object.entries(values.nominal_amounts ?? {}).map(([k, v]) => [denormalizeKey(k), v]),
        );

        const submitData = {
            id: selectedCashCollection.id,
            ...editedFields,
        };

        if (dirtyFields.nominal_amounts) {
            submitData.nominal_amounts = JSON.stringify(fixedNominals);
        }
        if (dirtyFields.date || dirtyFields.time) {
            const date = values.date ? new Date(values.date) : new Date();
            const [hours, minutes] = values.time?.split(':').map(Number) ?? [0, 0];
            date.setHours(hours, minutes, 0);

            submitData.date = date.toISOString();
        }

        if (dirtyFields.files && values.files) {
            const filesArray = Array.isArray(values.files) ? values.files : [values.files];
            submitData.files = filesArray;
        }

        await mutateAsync(submitData);
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        setIsEditModalOpen(isOpen);
    };

    return (
        <Dialog open={isEditModalOpen} onOpenChange={onOpenChange} modal>
            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-4">
                        <SquareIcon icon={ICON_MAP.EDIT} />
                        <div>
                            <DialogTitle>{formatMessage(cashCollectionsMessages.editCashCollection)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(cashCollectionsMessages.editCashCollectionDescription)}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogSeparator />

                <DialogBody>
                    <CashCollectionForm form={form} onSubmit={form.handleSubmit(onSubmit)} isEditModal={true} />
                </DialogBody>

                <DialogSeparator />

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>

                    <Button type="submit" onClick={form.handleSubmit(onSubmit)} className="flex-1">
                        {isPending && <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />}
                        {formatMessage(cashCollectionsMessages.editCashCollection)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { EditCashCollection };
